// AdminSoldePage.tsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Table,
  Button,
  Spinner,
  Alert,
  Modal,
  Form
} from "react-bootstrap";
import {
  FaUser,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaWallet,
  FaMoneyCheckAlt,
  FaHistory,
  FaEye,
  FaSync,
  FaDownload,
  FaPercentage,
  FaGift
} from "react-icons/fa";
import { getAllDiscountClaims } from "../services/Solde_service";

import '../styles/gestionsolde.css'

interface User {
  id: number;
  name: string;
  firstname: string;
  email: string;
  soldeRemise: number;
}

interface WithdrawRequest {
  id: number;
  userId: number;
  amount: number;
  status: "pending" | "approved" | "rejected";
  user: User;
  requestedAt: string;
  processedAt?: string;
  rib?: string;
  iban?: string;
  bic?: string
}

interface DiscountClaim {
  id: number;
  userId: number;
  user: {
    id: number;
    email: string;
    firstname: string;
    name: string;
  };
  totalAmount: number;
  percentageDiscount: number;
  pointsDiscount: number;
  creditNoteDiscount: number;
  usedPoints: number;
  usedAvoirsIds: number[];
  createdAt: string;
}

type TabType = "clients" | "withdrawals" | "discountHistory";

const AdminSoldePage = () => {
  const [activeTab, setActiveTab] = useState<TabType>("clients");
  const [clients, setClients] = useState<User[]>([]);
  const [withdrawRequests, setWithdrawRequests] = useState<WithdrawRequest[]>([]);
  const [discountHistory, setDiscountHistory] = useState<DiscountClaim[]>([]);
  const [loading, setLoading] = useState<{ clients: boolean; withdrawals: boolean; discountHistory: boolean }>({
    clients: false,
    withdrawals: false,
    discountHistory: false
  });
  const [error, setError] = useState<string | null>(null);
  const [processingRequestId, setProcessingRequestId] = useState<number | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');


  const [showDetailModal, setShowDetailModal] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');


  const [selectedClient, setSelectedClient] = useState<User & { rib?: string; iban?: string; bic?: string } | null>(null);


  const [filterName, setFilterName] = useState("");
  const [filterFirstname, setFilterFirstname] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterType, setFilterType] = useState<"all" | "percentage" | "points" | "credit">("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");


  const token = localStorage.getItem("token");

  const api = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);


  const handleViewDetails = (req: WithdrawRequest) => {
    setSelectedClient({
      ...req.user,
      rib: req.rib,
      iban: req.iban,
      bic: req.bic,
    });
    setShowDetailModal(true);
  };


  const closeDetailModal = () => {
    setSelectedClient(null);
    setShowDetailModal(false);
  };


  const fetchClients = useCallback(async () => {
    setLoading(prev => ({ ...prev, clients: true }));
    setError(null);
    try {
      const res = await api.get("/solde/users/solde-remise");
      setClients(res.data.data);
    } catch (err: any) {
      setError("Impossible de charger la liste des clients");
    } finally {
      setLoading(prev => ({ ...prev, clients: false }));
    }
  }, [api]);

  const fetchWithdrawRequests = useCallback(async () => {
    setLoading(prev => ({ ...prev, withdrawals: true }));
    setError(null);
    try {
      const res = await api.get("/solde/withdraw");
      setWithdrawRequests(res.data.data);
    } catch (err: any) {
      setError("Impossible de charger les demandes de retrait");
    } finally {
      setLoading(prev => ({ ...prev, withdrawals: false }));
    }
  }, [api]);

  const fetchDiscountHistory = useCallback(async () => {
    setLoading(prev => ({ ...prev, discountHistory: true }));
    setError(null);
    try {
      const result = await getAllDiscountClaims();
      if (result.success) {
        setDiscountHistory(result.data);
      } else {
        setError("Impossible de charger l'historique des remises");
      }
    } catch (err: any) {
      setError("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(prev => ({ ...prev, discountHistory: false }));
    }
  }, []);

  useEffect(() => {
    if (activeTab === "clients") {
      fetchClients();
    } else if (activeTab === "withdrawals") {
      fetchWithdrawRequests();
    } else if (activeTab === "discountHistory") {
      fetchDiscountHistory();
    }
  }, [activeTab, fetchClients, fetchWithdrawRequests, fetchDiscountHistory]);

  const filteredDiscountHistory = useMemo(() => {
  return discountHistory.filter((claim) => {
    const createdAt = new Date(claim.createdAt);
    const from = filterDateFrom ? new Date(filterDateFrom) : null;
    const to = filterDateTo ? new Date(filterDateTo) : null;

    if (from && createdAt < from) return false;
    if (to && createdAt > to) return false;

    if (
      filterName &&
      !claim.user.name.toLowerCase().includes(filterName.toLowerCase())
    ) return false;

    if (
      filterFirstname &&
      !claim.user.firstname.toLowerCase().includes(filterFirstname.toLowerCase())
    ) return false;

    if (
      filterEmail &&
      !claim.user.email.toLowerCase().includes(filterEmail.toLowerCase())
    ) return false;

    if (filterType !== "all") {
      if (filterType === "percentage" && claim.percentageDiscount <= 0) return false;
      if (filterType === "points" && claim.pointsDiscount <= 0) return false;
      if (filterType === "credit" && claim.creditNoteDiscount <= 0) return false;
    }

    return true;
  });
}, [
  discountHistory,
  filterName,
  filterFirstname,
  filterEmail,
  filterType,
  filterDateFrom,
  filterDateTo
]);


  const handleApprove = (id: number) => {
    setSelectedRequestId(id);
    setShowConfirmModal(true);
  };

  const confirmApprove = async () => {
    if (!selectedRequestId) return;

    const id = selectedRequestId;
    setProcessingRequestId(id);
    setShowConfirmModal(false);

    try {
      await api.put(`/solde/withdraw/${id}/approve`);
      alert("Retrait approuvé avec succès !");

      // Rafraîchir les données
      await fetchWithdrawRequests();
      await fetchClients();
    } catch (err: any) {
      alert(err.response?.data?.message || "Erreur lors de l'approbation");
    } finally {
      setProcessingRequestId(null);
      setSelectedRequestId(null);
    }
  };


  const handleReject = (id: number) => {
    setSelectedRequestId(id);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!selectedRequestId) return;

    const id = selectedRequestId;
    setProcessingRequestId(id);
    setShowRejectModal(false);

    try {
      await api.put(`/solde/withdraw/${id}/reject`, {
        reason: rejectReason, // optionnel côté backend
      });

      alert("Demande rejetée avec succès");

      await fetchWithdrawRequests();
      await fetchClients();
    } catch (err: any) {
      alert(err.response?.data?.message || "Erreur lors du rejet");
    } finally {
      setProcessingRequestId(null);
      setSelectedRequestId(null);
    }
  };

  const cancelApprove = () => {
    setShowConfirmModal(false);
    setSelectedRequestId(null);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  // Statistiques
  const totalSolde = useMemo(() =>
    clients.reduce((sum, client) => sum + client.soldeRemise, 0),
    [clients]
  );

  const totalPendingWithdrawals = useMemo(() =>
    withdrawRequests
      .filter(w => w.status === 'pending')
      .reduce((sum, w) => sum + Number(w.amount || 0),0),
    [withdrawRequests]
  );

  const totalApprovedWithdrawals = useMemo(
  () =>
    withdrawRequests
      .filter(w => w.status === 'approved')
      .reduce((sum, w) => sum + Number(w.amount || 0), 0),
  [withdrawRequests]
);


  // Filtrage
  const filteredWithdrawals = useMemo(() => {
    let filtered = withdrawRequests;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(w => w.status === filterStatus);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(w =>
        w.user?.name?.toLowerCase().includes(term) ||
        w.user?.email?.toLowerCase().includes(term) ||
        w.id.toString().includes(term)
      );
    }

    return filtered;
  }, [withdrawRequests, filterStatus, searchTerm]);

  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;

    const term = searchTerm.toLowerCase();
    return clients.filter(client =>
      client.name.toLowerCase().includes(term) ||
      client.firstname.toLowerCase().includes(term) ||
      client.email.toLowerCase().includes(term)
    );
  }, [clients, searchTerm]);


  const handleDownloadInvoice = async (withdrawId:any) => {
  try {
    const response = await axios.get(`/api_alo/withdraw/${withdrawId}/invoice`, {
      responseType: "blob", 
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `facture_retrait_${withdrawId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    console.error("Erreur téléchargement facture :", err);
    alert("Impossible de télécharger la facture.");
  }
};
return (
    <div>
      <Container fluid className="py-4" style={{ maxWidth: '100%', paddingLeft: '20px', paddingRight: '20px' }}>
        {/* Header */}
        <Row className="mb-5">
          <Col md={12}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h1 className="fw-bold mb-1" style={{ fontSize: '2rem', color: '#2A919D', letterSpacing: '-0.5px' }}>
                  Administration des Soldes
                </h1>
                <p className="text-muted mb-0">Gérez les soldes et demandes de retrait des clients</p>
              </div>
              <div className="d-flex gap-3">
                <Button
                  style={{ background: 'linear-gradient(135deg, #4BB0C0 0%, #2A919D 100%)', border: 'none' }}
                  className="d-flex align-items-center"
                  onClick={activeTab === 'clients' ? fetchClients : fetchWithdrawRequests}
                  disabled={loading.clients || loading.withdrawals}
                >
                  {loading.clients || loading.withdrawals ? <Spinner size="sm" /> : <FaSync className="me-2" />}
                  Actualiser
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Statistiques */}
        <Row className="mb-5">
          <Col md={3} className="mb-3">
            <Card className="border-0 shadow-sm h-100 hover-lift">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="text-muted mb-2">Total Clients</h6>
                    <h3 className="fw-bold mb-0" style={{ color: '#2A919D' }}>
                      {clients.length}
                    </h3>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '40px', height: '40px', backgroundColor: 'rgba(75, 176, 192, 0.1)' }}>
                    <FaUsers style={{ color: '#4BB0C0' }} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} className="mb-3">
            <Card className="border-0 shadow-sm h-100 hover-lift">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="text-muted mb-2">Solde Total</h6>
                    <h3 className="fw-bold mb-0" style={{ color: '#28a745' }}>
                      {formatCurrency(totalSolde)}
                    </h3>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '40px', height: '40px', backgroundColor: 'rgba(40, 167, 69, 0.1)' }}>
                    <FaWallet style={{ color: '#28a745' }} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} className="mb-3">
            <Card className="border-0 shadow-sm h-100 hover-lift">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="text-muted mb-2">Retraits En Attente</h6>
                    <h3 className="fw-bold mb-0" style={{ color: '#FF8C00' }}>
                      {formatCurrency(totalPendingWithdrawals)}
                    </h3>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '40px', height: '40px', backgroundColor: 'rgba(255, 140, 0, 0.1)' }}>
                    <FaClock style={{ color: '#FF8C00' }} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} className="mb-3">
            <Card className="border-0 shadow-sm h-100 hover-lift">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="text-muted mb-2">Retraits Approuvés</h6>
                    <h3 className="fw-bold mb-0" style={{ color: '#4BB0C0' }}>
                      {formatCurrency(totalApprovedWithdrawals)}
                    </h3>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '40px', height: '40px', backgroundColor: 'rgba(75, 176, 192, 0.1)' }}>
                    <FaMoneyCheckAlt style={{ color: '#4BB0C0' }} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabs et Recherche */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  {/* Tabs */}
                  <div className="d-flex gap-2">
                    <Button
                      variant={activeTab === 'clients' ? 'primary' : 'outline-primary'}
                      onClick={() => setActiveTab('clients')}
                      className="px-2"
                      style={{
                        backgroundColor: activeTab === 'clients' ? '#4BB0C0' : 'transparent',
                        borderColor: '#4BB0C0',
                        color: activeTab === 'clients' ? 'white' : '#4BB0C0'
                      }}
                    >
                      <FaUsers className="me-2" />
                      Clients
                    </Button>
                    <Button
                      variant={activeTab === 'withdrawals' ? 'primary' : 'outline-primary'}
                      onClick={() => setActiveTab('withdrawals')}
                      className="px-2 position-relative"
                      style={{
                        backgroundColor: activeTab === 'withdrawals' ? '#4BB0C0' : 'transparent',
                        borderColor: '#4BB0C0',
                        color: activeTab === 'withdrawals' ? 'white' : '#4BB0C0'
                      }}
                    >
                      <FaMoneyCheckAlt className="me-2" />
                      Demandes
                      {totalPendingWithdrawals > 0 && (
                        <Badge
                          bg="danger"
                          className="ms-2 position-absolute top-0 start-100 translate-middle rounded-pill"
                          style={{ fontSize: '0.6rem', padding: '0.25rem 0.5rem' }}
                        >
                          {withdrawRequests.filter(w => w.status === 'pending').length}
                        </Badge>
                      )}
                    </Button>
                    <Button
                      variant={activeTab === 'discountHistory' ? 'primary' : 'outline-primary'}
                      onClick={() => setActiveTab('discountHistory')}
                      className="px-2"
                      style={{
                        backgroundColor: activeTab === 'discountHistory' ? '#4BB0C0' : 'transparent',
                        borderColor: '#4BB0C0',
                        color: activeTab === 'discountHistory' ? 'white' : '#4BB0C0'
                      }}
                    >
                      <FaHistory className="me-2" />
                      Historique Remises
                    </Button>
                  </div>

                  {/* Barre de recherche */}
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      placeholder={`Rechercher ${activeTab === 'clients' ? 'un client...' : 'une demande...'}`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ minWidth: '250px' }}
                    />

                    {activeTab === 'withdrawals' && (
                      <Form.Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ width: '150px' }}
                      >
                        <option value="all">Tous les statuts</option>
                        <option value="pending">En attente</option>
                        <option value="approved">Approuvés</option>
                        <option value="rejected">Rejetés</option>
                      </Form.Select>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Error Message */}
        {error && (
          <Row className="mb-4">
            <Col md={12}>
              <Alert variant="danger" className="border-0 shadow-sm">
                <Alert.Heading>Erreur</Alert.Heading>
                <p>{error}</p>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Contenu */}
        {loading.clients && activeTab === 'clients' ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#4BB0C0' }} />
            <p className="mt-3 text-muted">Chargement des clients...</p>
          </div>
        ) : loading.withdrawals && activeTab === 'withdrawals' ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#4BB0C0' }} />
            <p className="mt-3 text-muted">Chargement des demandes...</p>
          </div>
        ) : loading.discountHistory && activeTab === 'discountHistory' ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#4BB0C0' }} />
            <p className="mt-3 text-muted">Chargement de l'historique...</p>
          </div>
        ) : (
          <>
            {/* Liste des Clients */}
            {activeTab === 'clients' && (
              <Row>
                <Col md={12}>
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-0 py-3">
                      <h5 className="mb-0 fw-bold" style={{ color: '#2A919D' }}>
                        Liste des Clients ({filteredClients.length})
                      </h5>
                    </Card.Header>

                    <Card.Body className="p-0">
                      {filteredClients.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="mb-3 opacity-50">
                            <FaUsers size={64} color="#dee2e6" />
                          </div>
                          <h6 className="text-muted mb-2">Aucun client trouvé</h6>
                          <p className="text-muted small mb-0">
                            {searchTerm ? "Aucun client ne correspond à votre recherche" : "Aucun client disponible"}
                          </p>
                        </div>
                      ) : (
                      <div
                        className="table-responsive"
                        style={{
                          maxHeight: '500px',  // hauteur max avant scroll vertical
                          overflowY: 'auto',   // scroll vertical
                          overflowX: 'auto',   // scroll horizontal si table trop large
                        }}
                      >
                        <Table hover className="mb-0" style={{ minWidth: '700px' }}>
                            <thead>
                              <tr className="border-bottom">
                                <th className="py-3 ps-4 fw-semibold" style={{ color: 'white',background:"#4BB0C0",textAlign:"center"}}>Client</th>
                                <th className="py-3 fw-semibold" style={{ color: 'white',background:"#4BB0C0",textAlign:"center"}}>Email</th>
                                <th className="py-3 fw-semibold" style={{ color: 'white',background:"#4BB0C0",textAlign:"center" }}>Solde</th>
                                <th className="py-3 fw-semibold" style={{ color: 'white',background:"#4BB0C0",textAlign:"center" }}>Statut</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredClients.map(client => (
                                <tr key={client.id} className="border-bottom">
                                 <td className="py-3 ps-4">
                                  <span className="d-inline-flex align-items-center gap-2">
                                    <span className="rounded-circle d-inline-flex align-items-center justify-content-center"
                                      style={{
                                        width: '36px',
                                        height: '36px',
                                        backgroundColor: 'rgba(75, 176, 192, 0.1)',
                                        color: '#4BB0C0'
                                      }}>
                                      <FaUser />
                                    </span>
                                    <span>
                                      <span className="fw-medium d-block">
                                        {client.firstname} {client.name}
                                      </span>
                                      <small className="text-muted">ID: #{client.id}</small>
                                    </span>
                                  </span>
                                </td>
                                  <td className="py-3" style={{textAlign:"center"}}>
                                      {client.email}
                                  </td>
                                  <td className="py-3" style={{textAlign:"center"}}>
                                    <span className="fw-bold" style={{
                                      color: client.soldeRemise > 0 ? '#28a745' : '#6c757d'
                                    }}>
                                      {formatCurrency(client.soldeRemise)}
                                    </span>
                                  </td>
                                  <td className="py-3" style={{textAlign:"center"}}>
                                    <Badge
                                      bg={client.soldeRemise > 0 ? 'success' : 'secondary'}
                                      className="px-3 py-1"
                                    >
                                      {client.soldeRemise > 0 ? 'Solde actif' : 'Solde nul'}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Demandes de Retrait */}
            {activeTab === 'withdrawals' && (
              <Row>
                <Col md={12}>
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-0 py-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 className="mb-0 fw-bold" style={{ color: '#2A919D' }}>
                          Demandes de Retrait ({filteredWithdrawals.length})
                        </h5>
                        <div className="text-muted">
                          Total en attente: <strong>{formatCurrency(totalPendingWithdrawals)}</strong>
                        </div>
                      </div>
                    </Card.Header>

                    <Card.Body className="p-0">
                      {filteredWithdrawals.length === 0 ? (
                        <div className="text-center py-5">
                          <div className="mb-3 opacity-50">
                            <FaMoneyCheckAlt size={64} color="#dee2e6" />
                          </div>
                          <h6 className="text-muted mb-2">Aucune demande trouvée</h6>
                          <p className="text-muted small mb-0">
                            {searchTerm || filterStatus !== 'all'
                              ? "Aucune demande ne correspond aux critères"
                              : "Aucune demande de retrait disponible"}
                          </p>
                        </div>
                      ) : (
                          <div
                            className="table-responsive"
                            style={{
                              maxHeight: '400px', // ajuste selon la hauteur désirée pour 5-10 lignes
                              overflowY: 'auto'
                            }}
                          >                          
                          <Table hover className="mb-0">
                            <thead>
                              <tr className="border-bottom">
                                <th className="py-3 fw-semibold text-center align-middle" style={{ color: '#495057' }}>Client</th>
                                <th className="py-3 fw-semibold text-center align-middle" style={{ color: '#495057' }}>Montant</th>
                                <th className="py-3 fw-semibold text-center align-middle" style={{ color: '#495057' }}>Statut</th>
                                <th className="py-3 fw-semibold text-center align-middle" style={{ color: '#495057' }}>Date</th>
                                <th className="py-3 pe-4 fw-semibold text-center align-middle" style={{ color: '#495057' }}>Actions</th>
                                <th className="text-center align-middle"></th>

                              </tr>
                            </thead>
                            <tbody>
                              {filteredWithdrawals.map(req => (
                                <tr key={req.id} className="border-bottom text-center align-middle">

                                  <td className="py-3">
                                  <span className="fw-medium d-block">
                                    {req.user?.firstname} {req.user?.name}
                                  </span>
                                  <small className="text-muted">{req.user?.email}</small>
                                </td>
                                  <td className="py-3">
                                    <span className="fw-bold" style={{ color: '#2A919D' }}>
                                      {formatCurrency(req.amount)}
                                    </span>
                                  </td>
                                  <td className="py-3">
                                    {req.status === 'pending' && (
                                      <span className="d-inline-flex align-items-center gap-1" style={{ backgroundColor: "#f4a261", padding: "5px 6px", borderRadius: "5px", color: "white" }}>
                                        <FaClock style={{ color: 'white' }} />
                                        En attente
                                      </span>
                                    )}

                                    {req.status === 'approved' && (
                                      <span className="d-inline-flex align-items-center gap-1" style={{ backgroundColor: "#4bb0c0", padding: "5px 6px", borderRadius: "5px", color: "white" }}>
                                        <FaCheckCircle style={{ color: 'white' }} />
                                        Approuvé
                                      </span>
                                    )}

                                    {req.status === 'rejected' && (
                                      <span className="d-inline-flex align-items-center gap-1" style={{ backgroundColor: "#dc3545", padding: "5px 6px", borderRadius: "5px", color: "white" }}>
                                        <FaTimesCircle style={{ color: 'white' }} />
                                        Rejeté
                                      </span>
                                    )}
                                  </td>
                                 <td className="py-3">
                                    <span className="fw-medium">
                                      {formatDate(req.requestedAt)}
                                    </span>
                                    {req.processedAt && (
                                      <>
                                        <br />
                                        <small className="text-muted">
                                          Traité le: {formatDate(req.processedAt)}
                                        </small>
                                      </>
                                    )}
                                  </td>
                                 <td className="py-3 text-center">
                          {req.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleApprove(req.id)}
                                disabled={processingRequestId === req.id}
                                className="me-2"
                                style={{ padding: "5px 10px" }}
                              >
                                <FaCheckCircle className="me-1" />
                                Approuver
                              </Button>

                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleReject(req.id)}
                                disabled={processingRequestId === req.id}
                                style={{ padding: "5px 10px" }}
                              >
                                <FaTimesCircle className="me-1" />
                                Rejeter
                              </Button>
                            </>
                          )}
                          
                          {req.status === "approved" && (
                            
                            <a
                              href={`${process.env.REACT_APP_API_URL}/solde/${req.id}/download-invoice`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Télécharger la facture"
                            >
                              <FaDownload style={{ cursor: 'pointer', fontSize: '18px' }} />
                            </a>
                          )}
                            </td>
                                    
                                  <td className="py-3 pe-4">
                                    <Button
                                      size="sm"
                                      onClick={() => handleViewDetails(req)}
                                    >
                                      <FaEye className="me-1" />
                                      Voir les détails
                                    </Button>
                                  </td>

                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Historique des Remises */}
            {activeTab === 'discountHistory' && (
              <Row>
                <Col md={12}>
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-0 py-3">
                      <h5 className="mb-0 fw-bold" style={{ color: '#2A919D' }}>
                          Historique des Réclamations de Remises ({filteredDiscountHistory.length})
                      </h5>
                    </Card.Header>

                    <div className="p-3 border-bottom bg-light justify-content-center text-center align-items-center">
                      <Row className="g-2">
                        <Col md={2}>
                          <Form.Control
                            placeholder="Nom"
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                          />
                        </Col>

                        <Col md={2}>
                          <Form.Control
                            placeholder="Prénom"
                            value={filterFirstname}
                            onChange={(e) => setFilterFirstname(e.target.value)}
                          />
                        </Col>

                        <Col md={3}>
                          <Form.Control
                            placeholder="Email"
                            value={filterEmail}
                            onChange={(e) => setFilterEmail(e.target.value)}
                          />
                        </Col>

                        <Col md={2}>
                          <Form.Select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value as any)}
                          >
                            <option value="all">Tous types</option>
                            <option value="percentage">Pourcentage</option>
                            <option value="points">Points</option>
                            <option value="credit">Avoir</option>
                          </Form.Select>
                        </Col>

                        <Col md={1}>
                          <Form.Control
                            type="date"
                            value={filterDateFrom}
                            onChange={(e) => setFilterDateFrom(e.target.value)}
                          />
                        </Col>

                        <Col md={1}>
                          <Form.Control
                            type="date"
                            value={filterDateTo}
                            onChange={(e) => setFilterDateTo(e.target.value)}
                          />
                        </Col>
                      </Row>
                      </div>

                    <Card.Body className="p-0">
                      {discountHistory.length === 0 ? (
                        <div className="text-center py-5">
                          <p className="text-muted">Aucune réclamation de remise trouvée</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <Table className="mb-0" hover>
                            <thead className="table-light">
                              <tr>
                                <th style={{ color: '#2A919D', fontWeight: 'bold' }}>Client</th>
                                <th style={{ color: '#2A919D', fontWeight: 'bold' }}>Date</th>
                                <th style={{ color: '#2A919D', fontWeight: 'bold' }}>Remise %</th>
                                <th style={{ color: '#2A919D', fontWeight: 'bold' }}>Remise Points</th>
                                <th style={{ color: '#2A919D', fontWeight: 'bold' }}>Remise Avoir</th>
                                <th style={{ color: '#2A919D', fontWeight: 'bold', textAlign: 'right' }}>Total (€)</th>
                              </tr>
                            </thead>
                           <tbody>
                              {filteredDiscountHistory.map((claim) => {
                                const percentageDiscount = Number(claim.percentageDiscount) || 0;
                                const pointsDiscount = Number(claim.pointsDiscount) || 0;
                                const creditNoteDiscount = Number(claim.creditNoteDiscount) || 0;
                                const totalAmount = Number(claim.totalAmount) || 0;
                                const usedPoints = Number(claim.usedPoints) || 0;

                                return (
                                  <tr key={claim.id}>
                                    <td>
                                      <span className="d-inline-flex align-items-center gap-2">
                                        <span
                                          className="rounded-circle d-inline-flex align-items-center justify-content-center"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            backgroundColor: '#E8F4F6',
                                            color: '#2A919D',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem'
                                          }}
                                        >
                                          {claim.user.firstname.charAt(0)}{claim.user.name.charAt(0)}
                                        </span>
                                        <span>
                                          <span className="fw-bold d-block mb-1">{claim.user.firstname} {claim.user.name}</span>
                                          <small className="text-muted">{claim.user.email}</small>
                                        </span>
                                      </span>
                                    </td>
                                    <td>
                                      <small className="text-muted">
                                        {new Date(claim.createdAt).toLocaleDateString('fr-FR', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric'
                                        })}
                                        <br />
                                        <strong style={{ fontSize: '0.85rem' }}>
                                          {new Date(claim.createdAt).toLocaleTimeString('fr-FR', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </strong>
                                      </small>
                                    </td>
                                    <td>
                                      {percentageDiscount > 0 ? (
                                        <span className="d-inline-flex align-items-center gap-1" style={{ backgroundColor: '#f7931e', padding: '5px 10px', borderRadius: '15px', color: 'white'}}>
                                          <FaPercentage />
                                          {percentageDiscount.toFixed(2)}€
                                        </span>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                    <td>
                                      {pointsDiscount > 0 ? (
                                        <span className="d-inline-flex align-items-center gap-1" style={{ backgroundColor: '#f7931e', padding: '5px 10px', borderRadius: '15px', color: 'white'}}>
                                          <FaGift />
                                          {usedPoints} pts /{pointsDiscount.toFixed(2)}€
                                        </span>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                    <td>
                                      {creditNoteDiscount > 0 ? (
                                        <span className="d-inline-flex align-items-center gap-1" style={{ backgroundColor: '#f4a261', padding: '5px 10px', borderRadius: '15px', color: 'white'}}>
                                          <FaCheckCircle />
                                          {creditNoteDiscount.toFixed(2)}€
                                        </span>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                      <strong style={{ color: '#2A919D', fontSize: '1.1rem' }}>
                                        {totalAmount.toFixed(2)}€
                                      </strong>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </>
        )}
      </Container>


      <Modal
        show={showDetailModal}
        onHide={closeDetailModal}
        centered
        className="border-0"
      >
        <Modal.Header closeButton className="border-bottom pb-3">
          <Modal.Title className="fw-bold fs-5" style={{ color: '#2A919D' }}>
            <i className="bi bi-person-circle me-2"></i>
            Détails du client
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-4">
          {selectedClient && (
            <div className="d-flex flex-column gap-3">
              {/* En-tête du client */}
              <div className="d-flex align-items-center gap-3 mb-3 p-3 bg-light rounded">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#E8F4F6',
                    color: '#2A919D',
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}
                >
                  {selectedClient.firstname.charAt(0)}{selectedClient.name.charAt(0)}
                </div>
                <div>
                  <h5 className="mb-1 fw-bold">{selectedClient.firstname} {selectedClient.name}</h5>
                  <div className="text-muted">{selectedClient.email}</div>
                </div>
              </div>

              {/* Section Solde - Mise en avant */}
              <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="text-muted small mb-1">Solde actuel</div>
                <div className={`fs-4 fw-bold ${selectedClient.soldeRemise >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatCurrency(selectedClient.soldeRemise)}
                </div>
              </div>

              {/* Informations bancaires */}
              <div className="mt-2">
                <h6 className="fw-semibold mb-3 text-muted text-uppercase small">
                  <i className="bi bi-credit-card me-2"></i>
                  Informations bancaires
                </h6>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span className="text-muted">RIB</span>
                    <span className="fw-medium font-monospace">{selectedClient.rib || '-'}</span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="text-muted">BIC/SWIFT</span>
                    <span className="fw-medium font-monospace">{selectedClient.bic || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top pt-3">
          <Button
            variant="light"
            onClick={closeDetailModal}
            className="px-4 py-2"
            style={{
              color: '#6c757d',
              border: '1px solid #dee2e6'
            }}
          >
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>


      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-danger">
            Rejeter la demande
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-3">
          <p className="mb-3 text-center">
            Êtes-vous sûr de vouloir <strong>rejeter</strong> cette demande de retrait ?
          </p>

          <Form.Group>
            <Form.Label>Motif du rejet (optionnel)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex : informations bancaires invalides"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={() => setShowRejectModal(false)}
          >
            Annuler
          </Button>

          <Button
            variant="danger"
            onClick={confirmReject}
          >
            <FaTimesCircle className="me-2" />
            Confirmer le rejet
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmation - EN DEHORS du Container */}
      <Modal show={showConfirmModal} onHide={cancelApprove} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: '#2A919D' }}>
            Confirmer l'approbation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4">
          <div className="text-center">
            <div className="rounded-circle d-inline-flex align-items-center justify-content-center p-3 mb-3"
              style={{ backgroundColor: 'rgba(75, 176, 192, 0.1)' }}>
              <FaCheckCircle size={32} style={{ color: '#4BB0C0' }} />
            </div>
            <p className="mb-0">Êtes-vous sûr de vouloir approuver cette demande de retrait ?</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={cancelApprove}
            className="px-4"
            style={{ color: '#6c757d' }}
          >
            Annuler
          </Button>
          <Button
            onClick={confirmApprove}
            className="px-4"
            style={{
              background: 'linear-gradient(135deg, #4BB0C0 0%, #2A919D 100%)',
              border: 'none'
            }}
          >
            <FaCheckCircle className="me-2" />
            Confirmer
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminSoldePage;